// JCS-C1: WHERE-fragment comparison parser.
//
// Detects the top-level comparison operator inside a boolean fragment and
// produces a `Comparison { lhs, operator, rhs? }` shape. Recognises the v1
// operator set (symbolic = / <> / != / < / > / <= / >= and keyword
// IS [NOT] NULL / LIKE / IN / BETWEEN). Engine-specific JSON operators
// (PG `@>` / `?`, mysql `->` / `->>`) are intentionally NOT parsed here —
// JCS-B1..B5 add structured grammar rules and JCS-C2 adds operator-lattice
// awareness; until then those fragments stay opaque-text.
//
// Each operand is classified via `classifyOperand` into one of `column` /
// `literal` / `expression`. Schema lookup + jsonClassification population
// happen in higher layers (JCS-C2/C3) that have a `SchemaSnapshot` in scope.
//
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice C1.

import type { ParseOptions, SqlRange } from '../../sql-grammar/model';
import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import { skipContextFor, trySkipNonCode } from '../classify/tokenSkip';
import { readIdentifier, foldIdentifier, isBareIdentStart } from '../scope/identifiers';
import type {
  Comparison,
  ComparisonOperator,
  OperandLiteralSqlType,
  OperandType,
} from '../types';

import { scanTopLevelKeywords, trimRange, type TopKeyword } from './scanner';
import { inferExpressionResultType } from './inferExpressionResultType';

// Scan + dispatch.
// eslint-disable-next-line complexity
export function parseComparisonFragment(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
  lineIdx: LineIndex,
): Comparison | undefined {
  const trimmed = trimRange(source, start, end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return undefined;
  const kws = scanTopLevelKeywords(source, trimmed.startOffset, trimmed.endOffset, options);

  // OR at top level → not a single comparison; leave opaque so consumers
  // know v1 doesn't decompose disjunction.
  for (const k of kws) {
    if (k.upper === 'OR') return undefined;
  }

  const keywordHit = findKeywordComparison(source, trimmed, kws, options);
  if (keywordHit) {
    return buildComparison(
      source,
      trimmed,
      lineIdx,
      options,
      keywordHit.opStart,
      keywordHit.opEnd,
      keywordHit.operator,
      keywordHit.unary,
    );
  }
  const symbolicHit = findSymbolicComparison(source, trimmed, options);
  if (symbolicHit) {
    return buildComparison(
      source,
      trimmed,
      lineIdx,
      options,
      symbolicHit.opStart,
      symbolicHit.opEnd,
      symbolicHit.operator,
      false,
    );
  }
  return undefined;
}

interface KeywordHit {
  readonly operator: ComparisonOperator;
  readonly opStart: number;
  readonly opEnd: number;
  readonly unary: boolean;
}

// eslint-disable-next-line complexity
function findKeywordComparison(
  source: string,
  trimmed: { startOffset: number; endOffset: number },
  kws: readonly TopKeyword[],
  options: ParseOptions,
): KeywordHit | undefined {
  for (let i = 0; i < kws.length; i++) {
    const k = kws[i];
    if (k.upper === 'IS') {
      const next = kws[i + 1];
      const nextNext = kws[i + 2];
      if (next?.upper === 'NOT' && nextNext?.upper === 'NULL') {
        return { operator: 'IS NOT NULL', opStart: k.start, opEnd: nextNext.end, unary: true };
      }
      if (next?.upper === 'NULL') {
        return { operator: 'IS NULL', opStart: k.start, opEnd: next.end, unary: true };
      }
      if (next?.upper === 'NOT') {
        return { operator: 'IS NOT', opStart: k.start, opEnd: next.end, unary: false };
      }
      return { operator: 'IS', opStart: k.start, opEnd: k.end, unary: false };
    }
    if (k.upper === 'NOT') {
      const next = kws[i + 1];
      if (next?.upper === 'LIKE') {
        return { operator: 'NOT LIKE', opStart: k.start, opEnd: next.end, unary: false };
      }
      if (next?.upper === 'ILIKE') {
        return { operator: 'NOT ILIKE', opStart: k.start, opEnd: next.end, unary: false };
      }
      if (next?.upper === 'IN') {
        return { operator: 'NOT IN', opStart: k.start, opEnd: next.end, unary: false };
      }
      if (next?.upper === 'BETWEEN') {
        return { operator: 'NOT BETWEEN', opStart: k.start, opEnd: next.end, unary: false };
      }
    }
    if (k.upper === 'LIKE') {
      return { operator: 'LIKE', opStart: k.start, opEnd: k.end, unary: false };
    }
    if (k.upper === 'ILIKE') {
      return { operator: 'ILIKE', opStart: k.start, opEnd: k.end, unary: false };
    }
    if (k.upper === 'IN') {
      return { operator: 'IN', opStart: k.start, opEnd: k.end, unary: false };
    }
    if (k.upper === 'BETWEEN') {
      return { operator: 'BETWEEN', opStart: k.start, opEnd: k.end, unary: false };
    }
  }
  void source;
  void trimmed;
  void options;
  return undefined;
}

interface SymbolicHit {
  readonly operator: ComparisonOperator;
  readonly opStart: number;
  readonly opEnd: number;
}

// Scan for top-level symbolic operators (=, <>, !=, <=, >=, <, >). Skips
// strings, comments, and parenthesised sub-expressions. Picks the LEFTMOST
// match — predicates like `a + b = c * 2` resolve `=` as the top-level op.
// eslint-disable-next-line complexity
function findSymbolicComparison(
  source: string,
  trimmed: { startOffset: number; endOffset: number },
  options: ParseOptions,
): SymbolicHit | undefined {
  const ctx = skipContextFor(options);
  let i = trimmed.startOffset;
  let depth = 0;
  while (i < trimmed.endOffset) {
    const ident = readIdentifier(source, i, trimmed.endOffset, options);
    if (ident && (ident.quoted || isBareIdentStart(source, i, options.engine))) {
      i = ident.end;
      continue;
    }
    const skipped = trySkipNonCode(source, i, trimmed.endOffset, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    if (depth === 0) {
      const two = source.slice(i, i + 2);
      const three = source.slice(i, i + 3);
      // MySQL null-safe-equals `<=>` — JCS-C1 leaves it opaque (the operator
      // surfaces as JSON-adjacent in C2; v1 keeps the fragment as text).
      if (three === '<=>') { i += 3; continue; }
      // PG/mysql/sqlite JSON arrow operators `->` / `->>` — JCS-B1/B2/B3
      // grammar work surfaces them structurally; v1 leaves them opaque by
      // skipping past so the LHS subsumes the arrow expression.
      if (three === '->>') { i += 3; continue; }
      if (two === '->') { i += 2; continue; }
      // `>>` shift operator (rare in WHERE clauses; treat as opaque).
      if (two === '>>') { i += 2; continue; }
      if (two === '<>' || two === '!=' || two === '<=' || two === '>=') {
        return { operator: two as ComparisonOperator, opStart: i, opEnd: i + 2 };
      }
      if (c === '=' || c === '<' || c === '>') {
        return { operator: c as ComparisonOperator, opStart: i, opEnd: i + 1 };
      }
    }
    i++;
  }
  return undefined;
}

function buildComparison(
  source: string,
  trimmed: { startOffset: number; endOffset: number },
  lineIdx: LineIndex,
  options: ParseOptions,
  opStart: number,
  opEnd: number,
  operator: ComparisonOperator,
  unary: boolean,
): Comparison {
  const lhsRange = trimWhitespace(source, trimmed.startOffset, opStart);
  const lhs = classifyOperand(source, lhsRange.startOffset, lhsRange.endOffset, options, lineIdx);
  const range: SqlRange = rangeFromOffsets(lineIdx, trimmed.startOffset, trimmed.endOffset);
  if (unary) {
    return { range, operator, lhs };
  }
  const rhsRange = trimWhitespace(source, opEnd, trimmed.endOffset);
  // Empty RHS → leave as expression-shaped placeholder so the comparison
  // shape stays well-formed; consumers can detect it via `rhs.text === ''`.
  const rhs: OperandType = rhsRange.startOffset >= rhsRange.endOffset
    ? { kind: 'expression', text: '' }
    : classifyOperand(source, rhsRange.startOffset, rhsRange.endOffset, options, lineIdx);
  return { range, operator, lhs, rhs };
}

// Whitespace-only trim. Unlike `trimRange` from scanner.ts, this does NOT
// skip strings / quoted identifiers / brackets — those are operand content,
// not non-code regions. Critical for symbolic-op RHS classification: e.g.
// `name = 'Alice'` RHS = `'Alice'`, not the trim-eaten empty range.
function trimWhitespace(
  source: string,
  start: number,
  end: number,
): { startOffset: number; endOffset: number } {
  let s = start;
  while (s < end) {
    const c = source[s];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { s++; continue; }
    break;
  }
  let e = end;
  while (e > s) {
    const c = source[e - 1];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { e--; continue; }
    break;
  }
  return { startOffset: s, endOffset: e };
}

// Classify a single operand by syntactic shape only — no schema lookup.
// `column` → bare identifier (optionally dotted), e.g. `a`, `t.col`, `"X".y`.
// `literal` → numeric / string / boolean / NULL token.
// `expression` → everything else (function calls, arithmetic, parens).
// eslint-disable-next-line complexity
function classifyOperand(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
  lineIdx: LineIndex,
): OperandType {
  const text = source.slice(start, end).trim();
  if (!text) return { kind: 'expression', text: '' };

  const literalType = classifyLiteral(text);
  if (literalType) {
    return { kind: 'literal', sqlType: literalType, text };
  }

  const columnRef = tryReadColumnRef(source, start, end, options, lineIdx);
  if (columnRef) return columnRef;

  // Infer a coarse result type so the parsed Comparison carries operand types
  // (consumed by the operator suggester; foundation for type-mismatch diags).
  const resultSqlType = inferExpressionResultType(text, options.engine);
  return resultSqlType === 'unknown'
    ? { kind: 'expression', text }
    : { kind: 'expression', text, resultSqlType };
}

const INTEGER_LITERAL = /^[+-]?\d+$/;
const NUMERIC_LITERAL = /^[+-]?\d+\.\d*([eE][+-]?\d+)?$|^[+-]?\.\d+([eE][+-]?\d+)?$|^[+-]?\d+[eE][+-]?\d+$/;
const TEXT_LITERAL = /^N?'.*'$/s;
const HEX_LITERAL = /^0[xX][0-9A-Fa-f]+$/;

function classifyLiteral(text: string): OperandLiteralSqlType | undefined {
  const upper = text.toUpperCase();
  if (upper === 'NULL') return 'null';
  if (upper === 'TRUE' || upper === 'FALSE') return 'boolean';
  if (INTEGER_LITERAL.test(text) || HEX_LITERAL.test(text)) return 'integer';
  if (NUMERIC_LITERAL.test(text)) return 'numeric';
  if (TEXT_LITERAL.test(text)) return 'text';
  return undefined;
}

// Detect a column reference: one or two identifier segments separated by a
// dot, optionally followed by trailing whitespace. Anything else (function
// call, arithmetic, qualified path with three+ parts, JSON path access)
// returns undefined — caller falls back to `expression`.
// eslint-disable-next-line complexity
function tryReadColumnRef(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
  lineIdx: LineIndex,
): OperandType | undefined {
  let i = skipWhitespace(source, start, end);
  const first = readIdentifier(source, i, end, options);
  if (!first) return undefined;
  i = first.end;
  i = skipWhitespace(source, i, end);
  // Optional dotted segments. Up to three (db.schema.table.column-ish) — we
  // accept anything that's a chain of identifier '.' identifier pairs.
  let last = first;
  let hadDot = false;
  while (i < end && source[i] === '.') {
    hadDot = true;
    i++;
    i = skipWhitespace(source, i, end);
    const part = readIdentifier(source, i, end, options);
    if (!part) return undefined;
    last = part;
    i = part.end;
    i = skipWhitespace(source, i, end);
  }
  // If anything remains after the identifier chain, it's not a bare column ref.
  if (i < end) return undefined;
  // Reserved-word-shaped operands (NULL / TRUE / FALSE) are handled by
  // classifyLiteral; reject them here defensively.
  const fullText = source.slice(start, end).trim();
  if (classifyLiteral(fullText)) return undefined;
  return {
    kind: 'column',
    ref: {
      qualified: {
        name: last.value,
        raw: last.raw,
        quoted: last.quoted,
        foldedName: foldIdentifier(last.value, last.quoted, options),
      },
      range: rangeFromOffsets(lineIdx, start, end),
      kind: 'column',
    },
    // jsonClassification stays undefined — populated by SR-RO consumers
    // (Phase C2/C3) that have a SchemaSnapshot in scope.
    ...(hadDot ? {} : {}),
  };
}

function skipWhitespace(source: string, start: number, end: number): number {
  let i = start;
  while (i < end) {
    const c = source[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    break;
  }
  return i;
}
